import {
  derivePrimaryRampStep,
  getOnAccentTextHex,
  PRIMARY_RAMP_STEPS,
  type PrimaryRampStep,
  sixDigitsColorHex,
} from './colorDerivation';
import { H2O_PRIMARY_VAR, HAIC_L1_PRIMARY_VAR, HAIC_L2_TEXT_ON_ACCENT_VAR } from './hostPrimaryVars';

const getInlineStyleValue = (root: HTMLElement, varName: string): string => root.style.getPropertyValue(varName).trim();

const hasInlinePrimaryStep = (root: HTMLElement, step: PrimaryRampStep): boolean => {
  const haic = getInlineStyleValue(root, HAIC_L1_PRIMARY_VAR(step));
  if (haic) return true;
  return Boolean(getInlineStyleValue(root, H2O_PRIMARY_VAR(step)));
};

const getInlinePrimaryStepValue = (root: HTMLElement, step: PrimaryRampStep): string | undefined => {
  const haic = getInlineStyleValue(root, HAIC_L1_PRIMARY_VAR(step));
  if (haic) return haic;
  const h2o = getInlineStyleValue(root, H2O_PRIMARY_VAR(step));
  return h2o || undefined;
};

const hasFullInlinePrimaryRamp = (root: HTMLElement): boolean =>
  PRIMARY_RAMP_STEPS.every((step) => hasInlinePrimaryStep(root, step));

/**
 * Applies host primary anchor to HAIC L1 primary ramp and L2 on-accent text (#8066 / ADR-005).
 * Writes new-tree tokens only; legacy `--h2o-*` remains owned by `@shared-legacy/theme`.
 */
export const applyHostPrimary = (anchorHex: string, root: HTMLElement = document.documentElement): void => {
  const normalizedAnchor = sixDigitsColorHex(anchorHex);
  const skipDerivation = hasFullInlinePrimaryRamp(root);

  PRIMARY_RAMP_STEPS.forEach((step) => {
    const varName = HAIC_L1_PRIMARY_VAR(step);
    const explicit = getInlinePrimaryStepValue(root, step);

    if (explicit) {
      if (!getInlineStyleValue(root, varName)) {
        root.style.setProperty(varName, explicit);
      }
      return;
    }

    if (skipDerivation) {
      return;
    }

    const derived = step === 500 ? normalizedAnchor : derivePrimaryRampStep(normalizedAnchor, step);
    root.style.setProperty(varName, derived);
  });

  const anchorForContrast = getInlinePrimaryStepValue(root, 500) ?? normalizedAnchor;

  if (!getInlineStyleValue(root, HAIC_L2_TEXT_ON_ACCENT_VAR)) {
    root.style.setProperty(HAIC_L2_TEXT_ON_ACCENT_VAR, getOnAccentTextHex(anchorForContrast));
  }
};
